import {
  AnyAction,
  createSlice,
  PayloadAction,
  ThunkDispatch,
} from "@reduxjs/toolkit";
import axios from "axios";
import { baseURL } from "../../../../api/agent";
import jwt_decode from "jwt-decode";
import { Setup } from "../../../../models/auth/Auth";
import { LoginFormValues } from "../../../../pages/Auth/Login";
import { PortalCustomerLogin } from "../../../../models/features/Customer/PortalLogin";
import { addGridSetting, removeGridSetting } from "../App/AppGridSlice";
import { AppGridId } from "../../../../models/app/AppGridModels";
import { GridColumnVisibilityModel } from "@mui/x-data-grid";
import { ProductType } from "../../../../models/features/Integration/DmsSetup";
import { initializePositions } from "../Dashboard/widgetSlice";
import {
  setPartsPortalEnable,
  setPartsPortalType,
} from "../ShoppingPortal/ShoppingPortalSlice";
import { PartsPortalType } from "../../../../models/features/ShopifySetting/ShoppingPortal/ShoppingPortal";
import { Dayjs } from "dayjs";
import { tokenService } from "../../../../services/token/tokenService";
export interface CustomerDetail {
  customerId?: number;
  email?: any;
  firstName?: string;
  lastName?: string;
  phone?: string;
  displayName?: string;
  address: any;
  organizationName?: string;
}

export interface AuthState {
  token?: string;
  userType?: string;
  setupId?: string;
  dmsSetup?: Setup;
  dmsDealerId?: string;
  dealerId?: string;
  everestDmsDealerId?: string;
  name?: string;
  userId?: string;
  targetWebAccessToken?: string;
  refreshToken?: string;
  customerData?: CustomerDetail;
  storeAccessToken?: string;
  role?: string;
  email?: string;
}

const initialState: AuthState = {};

export const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    addToken: (state, action: PayloadAction<any>) => {
      state.token = action.payload.token;
      state.userType = action.payload.userType;
      state.dmsDealerId = action.payload.dmsDealerId;
      state.dealerId = action.payload.dealerId;
      state.name = action.payload.name;
      state.targetWebAccessToken = action.payload.targetWebAccessToken;
      state.refreshToken = action.payload.refreshToken;
      state.customerData = action.payload?.customerData;
      state.storeAccessToken = action.payload.shopifyAccessToken;
      state.userId = action.payload.userId;
      state.everestDmsDealerId = action.payload.everestDmsDealerId;
      state.role = action.payload?.role;
      state.email = action.payload?.email;
    },
    updateToken: (state, action: PayloadAction<any>) => {
      if (action.payload) {
        state.targetWebAccessToken = action.payload.targetWebAccessToken;
        state.refreshToken = action.payload.refreshToken;
      } else {
        state.targetWebAccessToken = undefined;
        state.refreshToken = undefined;
      }
    },
    removeToken: (state) => {
      state.token = undefined;
      state.setupId = undefined;
      state.dmsSetup = undefined;
      state.userType = undefined;
      state.dmsDealerId = undefined;
      state.dealerId = undefined;
      state.name = "";
      state.targetWebAccessToken = undefined;
      state.refreshToken = undefined;
      state.customerData = undefined;
      state.storeAccessToken = undefined;
      state.everestDmsDealerId = undefined;
      state.userId = undefined;
      state.role = undefined;
      state.email = undefined;
    },
    setSetupId: (state, action: PayloadAction<string>) => {
      state.setupId = action.payload;
      state.dmsSetup = undefined;
    },
    setSetup: (state, action: PayloadAction<any>) => {
      state.dmsSetup = action.payload;
    },
    setSetupOnCreate: (state, action: PayloadAction<any>) => {
      state.dmsSetup = action.payload.setup;
      action.payload.callback();
    },
    addDmsLocation: (state, action: PayloadAction<any>) => {
      if (state.dmsSetup?.dmsLocations) {
        if (
          state.dmsSetup.dmsLocations.some(
            (x) => x.dealerId === action.payload.dealerId,
          )
        ) {
          state.dmsSetup.dmsLocations = state.dmsSetup.dmsLocations.map((x) =>
            x.dealerId === action.payload.dealerId ? action.payload : x,
          );
        } else {
          state.dmsSetup.dmsLocations = [
            ...state.dmsSetup.dmsLocations,
            action.payload,
          ];
        }
      }
    },
    removeDmsLocation: (state, action: PayloadAction<string>) => {
      if (
        state.dmsSetup?.dmsLocations &&
        state.dmsSetup?.dmsLocations.length > 0
      ) {
        state.dmsSetup.dmsLocations = state.dmsSetup.dmsLocations.filter(
          (loc) => loc.dealerId.toString() !== action.payload,
        );
      }
    },
    setSyncExisting: (state, action: PayloadAction<boolean>) => {
      if (state.dmsSetup) state.dmsSetup.syncExisting = action.payload;
    },
    setPaymentType: (state, action: PayloadAction<{ value: string }>) => {
      if (state.dmsSetup) {
        state.dmsSetup.paymentType = action.payload.value;
      }
    },
    setDMSSetupDefault: (state, action: PayloadAction<any>) => {
      if (state.dmsSetup) {
        state.dmsSetup.defaultShippingMiscId = action.payload.value1;
        state.dmsSetup.defaultShippingMfrId = action.payload.value2;
      }
    },
    setDMSPaymentMethodId: (
      state,
      action: PayloadAction<{ value: string }>,
    ) => {
      if (state.dmsSetup) {
        state.dmsSetup.dmsPaymentMethodId = action.payload.value;
      }
    },
    setDMSCustomerCategoryId: (
      state,
      action: PayloadAction<{ value: string }>,
    ) => {
      if (state.dmsSetup) {
        state.dmsSetup.customerCategoryId = action.payload.value;
      }
    },
    setPriceSynceType: (
      state,
      action: PayloadAction<{ type: any; productType: ProductType }>,
    ) => {
      if (state.dmsSetup) {
        if (action.payload.productType === ProductType.Wholegoods) {
          state.dmsSetup.priceType = action.payload.type;
        } else {
          state.dmsSetup.partsPriceType = action.payload.type;
        }
      }
    },
    setDashboardFilterDate: (
      state,
      action: PayloadAction<{ to: Dayjs; from: Dayjs; presetFilter: string }>,
    ) => {
      if (state.dmsSetup) {
        state.dmsSetup.dashboardPresetFilter = action.payload.presetFilter;
        state.dmsSetup.dashboardFilterFromDate = action.payload.from;
        state.dmsSetup.dashboardFilterToDate = action.payload.to;
      }
    },
    setBulkPriceSyncing: (
      state,
      action: PayloadAction<{ isSyncing: any; productType: ProductType }>,
    ) => {
      if (state.dmsSetup) {
        if (action.payload.productType === ProductType.Wholegoods) {
          state.dmsSetup.isWholegoodBulkPriceSyncing = action.payload.isSyncing;
        } else {
          state.dmsSetup.isPartsBulkPriceSyncing = action.payload.isSyncing;
        }
      }
    },
    setProductTypes: (state, action: PayloadAction<string[]>) => {
      if (state.dmsSetup) {
        state.dmsSetup.productTypes = action.payload;
      }
    },
    updateDmsLocation: (state, action: PayloadAction<any>) => {
      const updatedLocation = action.payload;
      if (state.dmsSetup?.dmsLocations) {
        if (state.dmsSetup?.dmsLocations) {
          state.dmsSetup.dmsLocations = state.dmsSetup.dmsLocations.map(
            (loc) =>
              loc.dealerId === updatedLocation.dealerId
                ? { ...loc, ...updatedLocation } // merges only new/changed fields from updatedLocation
                : loc,
          );
        }
      }
    },
  },
});

export const login =
  (values: LoginFormValues, onComplete: any, onError: any) =>
  async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    await axios
      .post(`${baseURL}api/Account/login`, {
        loginRequest: { ...values, userType: 1 },
      })
      .then((res) => {
        const token = res.data.entity.accessToken;
        const decodedToken = jwt_decode(token) as any;
        var profile = JSON.parse(decodedToken.profile);
        var dealerId = profile.dealerId.toString();
        let everestDmsDealerId = profile.everestDmsDealerId;
        var name = decodedToken.given_name;
        var email = decodedToken.email;
        const targetWebtoken = res.data.entity.targetWebAccessToken;
        const targetWebRefreshtoken = res.data.entity.refreshToken;
        tokenService.setTokens({
          accessToken: targetWebtoken,
          refreshToken: targetWebRefreshtoken,
        });
        const headers = { Authorization: `Bearer ${targetWebtoken}` };
        axios
          .post(
            `${baseURL}api/Store/GetDealerSetup`,
            {
              dmsDealerId:
                profile?.dmsDealerId?.toString() ?? values.dmsDealerId,
              userId: profile?.userId ?? "",
              everestDmsDealerId: everestDmsDealerId,
            },
            {
              headers,
            },
          )
          .then((resp) => {
            if (resp.data.status === 0) {
              dispatch(setSetupId(resp.data.entity?.id ?? ""));
              dispatch(setSetup(resp.data.entity ?? {}));
              // Clear old grid settings before loading new setup's settings
              dispatch(removeGridSetting());
              var gridSettings = resp.data.entity?.appGridSettings;
              if (gridSettings && gridSettings?.length > 0) {
                gridSettings.forEach((item: any) => {
                  if (item.gridId === "Dashboard_Cards") {
                    let data = JSON.parse(item?.setting);
                    dispatch(initializePositions(data));
                  } else {
                    let gridIdStr: keyof typeof AppGridId = item.gridId;
                    let parsedSetting = JSON.parse(item?.setting);
                    dispatch(
                      addGridSetting({
                        setting: {
                          gridId: AppGridId[gridIdStr],
                          columnVisibilityModel:
                            parsedSetting?.columnVisibilityModel,
                          filterSettings: parsedSetting?.filterSettings,
                          customerLinkSetting:
                            parsedSetting?.customerLinkSetting,
                        },
                      }),
                    );
                  }
                });
              }
              let IsPortalEnabled =
                resp.data.entity?.isPartsPortalEnabled ?? false;
              dispatch(setPartsPortalEnable({ isEnabled: IsPortalEnabled }));
              if (resp.data.entity?.partsPortalType !== null) {
                let PortalTypeStr: keyof typeof PartsPortalType =
                  resp.data.entity?.partsPortalType;
                dispatch(
                  setPartsPortalType({ type: PartsPortalType[PortalTypeStr] }),
                );
              }
            }
            dispatch(
              addToken({
                token: token,
                userType: "User",
                dmsDealerId:
                  profile?.dmsDealerId?.toString() ?? values.dmsDealerId,
                dealerId: dealerId,
                name: name,
                userId: profile?.userId ?? "",
                everestDmsDealerId: everestDmsDealerId,
                targetWebAccessToken: targetWebtoken,
                refreshToken: targetWebRefreshtoken,
                role: "Dealer",
                email: email,
              }),
            );

            onComplete();
          })
          .catch((error) => {
            onError();
          });
      })
      .catch((error) => {
        onError();
      });
  };

export const CustomerLoginApi =
  (values: LoginFormValues, onComplete: any, onError: any) =>
  async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    await axios
      .post(`${baseURL}api/Account/Customerlogin`, {
        loginRequest: { ...values, userType: 2 },
      })
      .then((res) => {
        if (res?.data?.status === 200) {
          const targetWebtoken = res.data.entity.targetWebAccessToken;
          const message = res.data.message;
          const headers = { Authorization: `Bearer ${targetWebtoken}` };
          axios
            .post(
              `${baseURL}api/Shopify/GetDealerSetup`,
              {
                dmsDealerId: values.dmsDealerId,
              },
              {
                headers,
              },
            )
            .then((resp) => {
              if (resp.data.status === 0) {
                dispatch(setSetupId(resp.data.entity.id ?? ""));
                dispatch(setSetup(resp.data.entity ?? {}));
              }
              const targetWebtoken = res.data.entity.targetWebAccessToken;
              tokenService.setTokens({
                accessToken: targetWebtoken,
                refreshToken: targetWebtoken,
              });
              dispatch(
                addToken({
                  token: res.data.entity.targetWebAccessToken,
                  userType: "Customer",
                  targetWebAccessToken: res.data.entity.targetWebAccessToken,
                  dmsDealerId: values.dmsDealerId,
                  everestDmsDealerId: values.dmsDealerId,
                  dealerId: "",
                  name: res.data.entity.customer?.organizationName,
                  customerData: res.data.entity.customer,
                }),
              );
              onComplete(message);
            })
            .catch((error) => {
              onError();
            });
        } else {
          onError();
        }
      })
      .catch((error) => {
        onError();
      });
  };

export const AdminLoginApi =
  (values: LoginFormValues, onComplete: any, onError: any) =>
  async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    await axios
      .post(`${baseURL}api/Account/login`, {
        loginRequest: { ...values, userType: 0 },
      })
      .then((res) => {
        if (res?.data?.status === 200) {
          const decodedToken = jwt_decode(
            res.data.entity.targetWebAccessToken,
          ) as any;
          var name = decodedToken.name;
          //var adminData = JSON.parse(decodedToken);
          console.log(decodedToken, "adminData");
          const targetWebtoken = res.data.entity.targetWebAccessToken;
          const targetWebRefreshtoken = res.data.entity.refreshToken;
          tokenService.setTokens({
            accessToken: targetWebtoken,
            refreshToken: targetWebRefreshtoken,
          });
          dispatch(
            addToken({
              token: res.data.entity.targetWebAccessToken,
              userType: "Admin",
              targetWebAccessToken: res.data.entity.targetWebAccessToken,
              refreshToken: res.data.entity.refreshToken,
              dmsDealerId: values.dmsDealerId,
              dealerId: "",
              name: name,
              customerData: res.data.entity.customer,
              role: decodedToken.role,
            }),
          );
          onComplete();
        } else {
          onError();
        }
      })
      .catch((error) => {
        onError();
      });
  };

export const PortalCustomerLoginApi =
  (values: PortalCustomerLogin, onComplete: any, onError: any) =>
  async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    await axios
      .post(`${baseURL}api/Account/PortalCustomerlogin`, {
        loginRequest: { ...values, userType: 3 },
      })
      .then((res) => {
        if (res?.data?.status === 200) {
          dispatch(
            addToken({
              token: res.data.entity.targetWebAccessToken,
              userType: "PortalCustomer",
              targetWebAccessToken: res.data.entity.targetWebAccessToken,
              dealerId: "",
              shopifyAccessToken: res.data.entity.accessToken,
              customerData: res.data.entity.customerDetail,
            }),
          );
          onComplete();
        } else {
          onError();
        }
      })
      .catch((error) => {
        onError();
      });
  };

export const PortalCreateCustomerApi =
  (values: PortalCustomerLogin, onComplete: any, onError: any) =>
  async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    await axios
      .post(`${baseURL}api/Account/PortalCreateCustomer`, {
        customerRequest: { ...values },
      })
      .then((res) => {
        if (res?.data?.status === 200) {
          dispatch(
            addToken({
              token: res.data.entity.targetWebAccessToken,
              userType: "PortalCustomer",
              targetWebAccessToken: res.data.entity.targetWebAccessToken,
              dealerId: "",
              shopifyAccessToken: res.data.entity.accessToken,
              customerData: res.data.entity.customerDetail,
            }),
          );
          onComplete();
        } else {
          onError(res.data.message);
        }
      })
      .catch((error) => {
        onError();
      });
  };

export const RefreshTokenApi =
  (token: any, onComplete: any) =>
  async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    await axios
      .post(`${baseURL}api/Account/RefreshToken`, {
        refreshToken: token,
      })
      .then((res) => {
        if (res?.data?.status === 200) {
          dispatch(
            updateToken({
              targetWebAccessToken: res.data.entity.targetWebAccessToken,
              refreshToken: res.data.entity.refreshToken,
            }),
          );
          tokenService.setTokens({
            accessToken: res.data.entity.targetWebAccessToken,
            refreshToken: res.data.entity.refreshToken,
          });
          onComplete();
        } else {
          console.log(res.data.message);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

export const RegenerateTokenApi =
  (values: { dmsSetupId: string; accessToken: string }, onComplete: any) =>
  async (dispatch: ThunkDispatch<{}, {}, AnyAction>, getState: any) => {
    const url = values.dmsSetupId
      ? `${baseURL}api/Account/RegenerateToken?dmsSetupId=${values.dmsSetupId}`
      : `${baseURL}api/Account/RegenerateToken`;
    const token =
      values.accessToken || getState().authReducer.targetWebAccessToken;
    await axios
      .post(url, {}, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (res?.data?.status === 200) {
          dispatch(
            updateToken({
              targetWebAccessToken: res.data.entity.targetWebAccessToken,
              refreshToken: res.data.entity.refreshToken,
            }),
          );
          tokenService.setTokens({
            accessToken: res.data.entity.targetWebAccessToken,
            refreshToken: res.data.entity.refreshToken,
          });
          onComplete();
        } else {
          console.log(res.data.message);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

export const SwitchSetupApi =
  (
    values: { dmsSetupId: string; accessToken: string; userId: string },
    onComplete: any,
    onError: any,
  ) =>
  async (dispatch: ThunkDispatch<{}, {}, AnyAction>, getState: any) => {
    const token =
      values.accessToken || getState().authReducer.targetWebAccessToken;

    await axios
      .post(
        `${baseURL}api/Account/RegenerateToken?dmsSetupId=${values.dmsSetupId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((res) => {
        // Reversed the logic where we are using idms token while switching
        // b/w setups because idms token is valid only for 10 mins, and we don't
        // refresh idms token functionality.

        // const token = res.data.entity.accessToken;
        // const decodedToken = jwt_decode(token) as any;
        // var profile = JSON.parse(decodedToken.profile);
        // var dealerId = profile.dealerId.toString();
        // let everestDmsDealerId = profile.everestDmsDealerId;
        // var name = decodedToken.given_name;
        // var email = decodedToken.email;
        const targetWebtoken = res.data.entity.targetWebAccessToken;
        const targetWebRefreshtoken = res.data.entity.refreshToken;
        tokenService.setTokens({
          accessToken: targetWebtoken,
          refreshToken: targetWebRefreshtoken,
        });
        const headers = { Authorization: `Bearer ${targetWebtoken}` };
        axios
          .get(
            `${baseURL}api/DmsSetup/GetDealerSetupByDmsSetupId?dmsSetupId=${values.dmsSetupId}&userId=${values.userId}`,
            {
              headers,
            },
          )
          .then((resp) => {
            if (resp.data.status === 200) {
              dispatch(setSetupId(resp.data.entity?.id ?? ""));
              dispatch(setSetup(resp.data.entity ?? {}));
            }
            dispatch(removeGridSetting());
            var gridSettings = resp.data.entity?.appGridSettings;
            if (gridSettings && gridSettings?.length > 0) {
              gridSettings.forEach((item: any) => {
                if (item.gridId === "Dashboard_Cards") {
                  let data = JSON.parse(item?.setting);
                  dispatch(initializePositions(data));
                } else {
                  let gridIdStr: keyof typeof AppGridId = item.gridId;
                  let parsedSetting = JSON.parse(item?.setting);
                  dispatch(
                    addGridSetting({
                      setting: {
                        gridId: AppGridId[gridIdStr],
                        columnVisibilityModel:
                          parsedSetting.columnVisibilityModel || parsedSetting,
                        filterSettings: parsedSetting.filterSettings,
                      },
                    }),
                  );
                }
              });
            }
            dispatch(
              updateToken({
                targetWebAccessToken: targetWebtoken,
                refreshToken: targetWebRefreshtoken,
              }),
            );

            onComplete();
          })
          .catch((error) => {
            onError();
          });
      })
      .catch((error) => {
        onError();
      });
  };

export const {
  addToken,
  updateToken,
  removeToken,
  setSetupId,
  setSetup,
  setSetupOnCreate,
  setPaymentType,
  setPriceSynceType,
  setSyncExisting,
  addDmsLocation,
  removeDmsLocation,
  setDMSCustomerCategoryId,
  setDMSSetupDefault,
  setDMSPaymentMethodId,
  setDashboardFilterDate,
  setBulkPriceSyncing,
  setProductTypes,
  updateDmsLocation,
} = authSlice.actions;
export const authReducer = authSlice.reducer;
